// The Billing tab's "Trials and credits" card (SCR-009, REQ-036): the customer's trials with what
// each has used; a trial's row opens its summary with the burn-down and Cancel trial (which asks
// first); and — for
// `billing:write` — the Add form. Trials
// only apply to a billed month; a not-billed customer sees why instead.
import { useState } from "preact/hooks";
import { useApi } from "@nexus/ui";
import { ApiError } from "@nexus/ui";
import { ConfirmDialog } from "@nexus/ui";
import { errorId, Field, InputAffix } from "@nexus/ui";
import { hasErrors } from "@nexus/ui";
import { Card, Chip } from "@nexus/ui";
import { ROW_OPEN_CLASS, rowClickHandler } from "@nexus/ui";
import { RowDetail } from "@nexus/ui";
import { ErrorBanner } from "@nexus/ui";
import {
  buildCreateTrialRequest,
  emptyTrialForm,
  trialRow,
  trialStatusChip,
  validateTrialForm,
  type Trial,
  type TrialFormValues,
} from "./model";
import { TrialBurndown } from "./trial-burndown";

export interface TrialsCardProps {
  orgId: string;
  billed: boolean;
  canWrite: boolean;
  trials: readonly Trial[];
  onTrials(trials: readonly Trial[]): void;
}

function unexpected(error: unknown): ApiError {
  return error instanceof ApiError ? error : new ApiError(0, "internal", "Something went wrong.");
}

export function TrialsCard({ orgId, billed, canWrite, trials, onTrials }: TrialsCardProps) {
  const client = useApi();
  const [selected, setSelected] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState<TrialFormValues>(emptyTrialForm());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [cancelling, setCancelling] = useState<Trial | null>(null);

  const errors = validateTrialForm(form);
  const unbounded = form.startsOn.trim() === "" && form.endsOn.trim() === "" && form.creditUsd.trim() === "" && form.tokenAllowance.trim() === "";

  const addTrial = async () => {
    setBusy(true);
    setError(null);
    try {
      const trial = await client.post("/v1/admin/orgs/{orgId}/trials", { path: { orgId }, body: buildCreateTrialRequest(form) });
      onTrials([trial, ...trials]);
      setForm(emptyTrialForm());
      setAdding(false);
    } catch (err) {
      setError(unexpected(err));
    } finally {
      setBusy(false);
    }
  };

  // The summary closes while the confirmation is up and reopens on the outcome, a refusal included.
  const cancelTrial = async (trialId: string) => {
    setBusy(true);
    setError(null);
    try {
      const updated = await client.post("/v1/admin/orgs/{orgId}/trials/{trialId}/cancel", { path: { orgId, trialId } });
      onTrials(trials.map((t) => (t.id === updated.id ? updated : t)));
    } catch (err) {
      setError(unexpected(err));
    } finally {
      setBusy(false);
      setCancelling(null);
      setSelected(trialId);
    }
  };

  const selectedTrial = trials.find((t) => t.id === selected) ?? null;

  const shownError = (field: keyof TrialFormValues) => (field === "startsOn" && unbounded ? undefined : errors[field]);
  const invalid = (field: keyof TrialFormValues) => (shownError(field) ? true : undefined);
  const described = (field: keyof TrialFormValues, id: string) => (shownError(field) ? errorId(id) : undefined);

  return (
    <Card
      title="Trials and credits"
      help="terms.trials"
      actions={
        billed &&
        canWrite && (
          <button type="button" class="button button-sm" aria-expanded={adding} onClick={() => setAdding((open) => !open)}>
            {adding ? "Close" : "New trial"}
          </button>
        )
      }
    >
      {!billed ? (
        <p class="card-note">Trials apply only to billed months. Switch this customer to billed under Who pays whom first.</p>
      ) : trials.length === 0 ? (
        <p class="card-note">None. A trial gives free usage up to a dollar or token limit between two dates.</p>
      ) : (
        <div class="table-scroll">
          <table class="data-table data-table-compact">
            <caption class="visually-hidden">Trials</caption>
            <thead>
              <tr>
                <th scope="col">Limits</th>
                <th scope="col">Status</th>
                <th scope="col">Used</th>
              </tr>
            </thead>
            <tbody>
              {trials.map((trial) => {
                const row = trialRow(trial, canWrite);
                const chip = trialStatusChip(trial.status);
                return (
                  <tr key={row.id} class="row-clickable" onClick={rowClickHandler(() => setSelected(row.id))}>
                    <td>
                      <button type="button" class={ROW_OPEN_CLASS} aria-label={`Trial: ${row.boundsSummary}, ${chip.label}`} />
                      {row.boundsSummary}
                    </td>
                    <td>
                      <Chip tone={chip.tone}>{chip.label}</Chip>
                    </td>
                    <td>
                      {row.creditConsumedDisplay.text}
                      <span class="cell-sub">{row.tokensConsumed.toLocaleString("en-US")} tokens</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {error && !selectedTrial && <ErrorBanner error={error} />}
      {billed && selectedTrial && (
        <TrialDetail orgId={orgId} trial={selectedTrial} canWrite={canWrite} error={error} onCancel={() => (setSelected(null), setCancelling(selectedTrial))} onClose={() => setSelected(null)} />
      )}
      <ConfirmDialog
        title="Cancel trial"
        open={cancelling !== null}
        message={cancelling ? `Cancelling the trial (${trialRow(cancelling, canWrite).boundsSummary}) stops it applying from now on; it cannot be resumed.` : ""}
        confirmLabel="Cancel trial"
        busy={busy}
        onConfirm={() => cancelling && void cancelTrial(cancelling.id)}
        onClose={() => setCancelling(null)}
      />

      {billed && canWrite && adding && (
        <div class="subform" role="group" aria-labelledby="new-trial-title">
          <h3 id="new-trial-title">Add a trial</h3>
          <div class="form-grid form-grid-2">
            <Field id="trial-starts" label="Starts on" help="terms.trialStarts" error={shownError("startsOn")}>
              <input
                id="trial-starts"
                type="date"
                value={form.startsOn}
                aria-invalid={invalid("startsOn")}
                aria-describedby={described("startsOn", "trial-starts")}
                onInput={(e) => setForm({ ...form, startsOn: e.currentTarget.value })}
              />
            </Field>
            <Field id="trial-ends" label="Ends on" help="terms.trialEnds" error={shownError("endsOn")}>
              <input
                id="trial-ends"
                type="date"
                value={form.endsOn}
                aria-invalid={invalid("endsOn")}
                aria-describedby={described("endsOn", "trial-ends")}
                onInput={(e) => setForm({ ...form, endsOn: e.currentTarget.value })}
              />
            </Field>
            <Field id="trial-credit" label="Credit" help="terms.trialCredit" error={shownError("creditUsd")}>
              <InputAffix prefix="$">
                <input
                  id="trial-credit"
                  type="text"
                  inputMode="decimal"
                  value={form.creditUsd}
                  aria-invalid={invalid("creditUsd")}
                  aria-describedby={described("creditUsd", "trial-credit")}
                  onInput={(e) => setForm({ ...form, creditUsd: e.currentTarget.value })}
                />
              </InputAffix>
            </Field>
            <Field id="trial-allowance" label="Token allowance" help="terms.trialAllowance" error={shownError("tokenAllowance")}>
              <input
                id="trial-allowance"
                type="text"
                inputMode="numeric"
                value={form.tokenAllowance}
                aria-invalid={invalid("tokenAllowance")}
                aria-describedby={described("tokenAllowance", "trial-allowance")}
                onInput={(e) => setForm({ ...form, tokenAllowance: e.currentTarget.value })}
              />
            </Field>
          </div>
          <Field id="trial-models" label="Models" help="terms.trialModels" hint="Comma-separated Bedrock model ids; leave empty for every model.">
            <input
              id="trial-models"
              type="text"
              class="input-mono"
              value={form.models}
              aria-describedby="trial-models-hint"
              onInput={(e) => setForm({ ...form, models: e.currentTarget.value })}
            />
          </Field>
          {unbounded && <p class="card-note">Give the trial a window, a credit or a token allowance.</p>}
          <div class="form-actions-start">
            <button type="button" class="button button-primary" disabled={busy || hasErrors(errors)} onClick={() => void addTrial()}>
              {busy ? "Adding…" : "Add trial"}
            </button>
          </div>
        </div>
      )}
    </Card>
  );
}

interface TrialDetailProps {
  orgId: string;
  trial: Trial;
  canWrite: boolean;
  /** A refused cancel, shown in the summary that offered it. */
  error: ApiError | null;
  onCancel(): void;
  onClose(): void;
}

function TrialDetail({ orgId, trial, canWrite, error, onCancel, onClose }: TrialDetailProps) {
  const row = trialRow(trial, canWrite);
  const chip = trialStatusChip(trial.status);
  return (
    <RowDetail
      title="Trial"
      open
      onClose={onClose}
      class="dialog-wide"
      facts={[
        { label: "Limits", value: row.boundsSummary },
        { label: "Status", value: <Chip tone={chip.tone}>{chip.label}</Chip> },
        { label: "Credit used", value: row.creditConsumedDisplay.text },
        { label: "Tokens used", value: row.tokensConsumed.toLocaleString("en-US") },
      ]}
      actions={
        row.canCancel && (
          <button type="button" class="button button-danger" onClick={onCancel}>
            Cancel trial
          </button>
        )
      }
    >
      {error && <ErrorBanner error={error} />}
      <TrialBurndown orgId={orgId} trialId={trial.id} />
    </RowDetail>
  );
}
